/**
 * 재생 기록 서버 동기화 훅
 *
 * 주요 기능:
 * - 세션 스토리지의 재생 기록과 서버 데이터(장바구니, 구매 내역) 동기화
 * - 각 트랙의 장바구니 포함 여부(isInCart) 및 구매 여부(isPurchased) 상태 업데이트
 * - 중복 업데이트 방지를 위한 동기화 키 관리
 *
 * 자동 동기화 시점:
 * - 사용자 로그인 시 (isLoggedIn이 true가 될 때 쿼리 활성화)
 * - 장바구니 항목 추가/제거 시 (cart query 무효화)
 * - 구매 완료 시 (purchases query 무효화)
 *
 * 동작 흐름:
 * 1. 서버에서 장바구니 및 구매 내역 데이터 가져오기
 * 2. 트랙 ID 추출 및 정렬
 * 3. 동기화 키 생성 및 중복 체크
 * 4. 세션 스토리지의 재생 기록 업데이트
 *
 * 사용처:
 * - App.tsx에서 전역적으로 호출하여 재생 기록 상태 동기화
 */

import { useEffect, useRef } from "react";
import { useCart } from "@/queries/useCartQueries";
import { usePurchases } from "@/queries/usePurchaseQueries";
import { useAuthStore } from "@/stores/authStore";
import { getPlayHistory, savePlayHistory } from "@/utils/playHistoryUtils";
import { logger } from "@/utils/logger";

/**
 * 재생 기록을 서버 데이터와 동기화하는 커스텀 훅
 *
 * 장바구니 및 구매 내역 변경 시 자동으로 세션 스토리지의 재생 기록 동기화
 */
export const useSyncPlayHistoryWithServer = () => {
  const { isLoggedIn } = useAuthStore();
  const { data: cartResponse } = useCart();
  const { data: purchasesResponse } = usePurchases(1);
  const prevSyncKey = useRef<string>("");

  useEffect(() => {
    // 로그인하지 않은 경우 동기화 건너뛰기
    if (!isLoggedIn) {
      prevSyncKey.current = "";
      return;
    }

    // 서버에서 장바구니 트랙 ID 추출
    const cartTrackIds: string[] = [];
    if (cartResponse?.success && cartResponse.data?.results) {
      cartResponse.data.results.forEach((item) => {
        if (item.track?.track_number) {
          cartTrackIds.push(item.track.track_number.toString());
        }
      });
    }

    // 서버에서 구매한 트랙 ID 추출
    // 참고: purchasesResponse.data는 주문 배열 또는 results[]를 가진 페이지네이션 객체일 수 있음
    const purchasedTrackIds: string[] = [];
    if (purchasesResponse?.success && purchasesResponse.data) {
      const orders = Array.isArray(purchasesResponse.data)
        ? purchasesResponse.data
        : (purchasesResponse.data.results ?? []);

      orders.forEach((order) => {
        // 각 주문은 여러 항목을 포함
        if (order?.items && Array.isArray(order.items)) {
          order.items.forEach((item) => {
            if (item.track?.track_number) {
              purchasedTrackIds.push(item.track.track_number.toString());
            }
          });
        }
      });
    }

    logger.info("[재생 기록 동기화] 서버에서 ID 추출 완료:", {
      cartTrackIds,
      purchasedTrackIds,
      cartResponseStatus: cartResponse?.success,
      purchasesResponseStatus: purchasesResponse?.success,
    });

    // 중복 업데이트 방지를 위한 동기화 키 생성
    const syncKey = `${[...cartTrackIds].sort().join(",")}_${[...purchasedTrackIds].sort().join(",")}`;

    if (syncKey === prevSyncKey.current) {
      logger.info("[재생 기록 동기화] 건너뛰기 - 동기화 키 변경 없음");
      return;
    }

    // 서버 데이터로 세션 스토리지 업데이트
    const currentHistory = getPlayHistory();
    const updatedHistory = currentHistory.map((track) => ({
      ...track,
      isInCart: cartTrackIds.includes(track.id),
      isPurchased: purchasedTrackIds.includes(track.id),
    }));

    logger.info("[재생 기록 동기화] 세션 스토리지 업데이트 중:", {
      totalTracks: updatedHistory.length,
      tracksInCart: updatedHistory.filter((t) => t.isInCart).length,
      tracksPurchased: updatedHistory.filter((t) => t.isPurchased).length,
    });

    savePlayHistory(updatedHistory);
    prevSyncKey.current = syncKey;
  }, [isLoggedIn, cartResponse, purchasesResponse]);
};
